const DB_NAME = 'optika-offline';
const DB_VERSION = 1;

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('inventory')) {
        const inv = db.createObjectStore('inventory', { keyPath: 'id' });
        inv.createIndex('category', 'category');
      }
      if (!db.objectStoreNames.contains('customers')) {
        db.createObjectStore('customers', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('sales_queue')) {
        const sq = db.createObjectStore('sales_queue', { keyPath: 'localId', autoIncrement: true });
        sq.createIndex('synced', 'synced');
      }
      if (!db.objectStoreNames.contains('dashboard')) {
        db.createObjectStore('dashboard', { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings', { keyPath: 'key' });
      }
    };
  });
  return dbPromise;
}

function get(storeName, key) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const req = db.transaction(storeName).objectStore(storeName).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }));
}

function getAll(storeName) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const req = db.transaction(storeName).objectStore(storeName).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }));
}

function getAllByIndex(storeName, indexName, value) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const req = db.transaction(storeName).objectStore(storeName).index(indexName).getAll(value);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }));
}

function put(storeName, value) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const req = db.transaction(storeName, 'readwrite').objectStore(storeName).put(value);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }));
}

function add(storeName, value) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const req = db.transaction(storeName, 'readwrite').objectStore(storeName).add(value);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }));
}

function putAll(storeName, items) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    items.forEach(item => store.put(item));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }));
}

function deleteByIndexValue(storeName, indexName, value) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const req = store.index(indexName).getAllKeys(value);
    req.onsuccess = () => {
      req.result.forEach(key => store.delete(key));
      tx.oncomplete = () => resolve();
    };
    req.onerror = () => reject(req.error);
    tx.onerror = () => reject(tx.error);
  }));
}

// ── INVENTORY ─────────────────────────────────────────────────────────────────

export const cacheInventory = (items) => putAll('inventory', items);
export const getOfflineInventory = () => getAll('inventory');
export const updateOfflineItemQty = async (itemId, newQty) => {
  const item = await get('inventory', itemId);
  if (item) await put('inventory', { ...item, quantity: newQty });
};

// ── CUSTOMERS ─────────────────────────────────────────────────────────────────

export const cacheCustomers = (customers) => putAll('customers', customers);
export const getOfflineCustomers = () => getAll('customers');

// ── SALES QUEUE ───────────────────────────────────────────────────────────────

export const queueSale = (saleData) => {
  // eslint-disable-next-line no-unused-vars
  const { localId: _drop, ...data } = saleData;
  return add('sales_queue', { ...data, synced: 0, queuedAt: new Date().toISOString() });
};

export const getPendingSales = () => getAllByIndex('sales_queue', 'synced', 0);

export const markSaleSynced = async (localId) => {
  const sale = await get('sales_queue', localId);
  if (sale) await put('sales_queue', { ...sale, synced: 1 });
};

export const deleteSyncedSales = () => deleteByIndexValue('sales_queue', 'synced', 1);

// ── DASHBOARD CACHE ───────────────────────────────────────────────────────────

export const cacheDashboard = (cacheKeyOrData, maybeData) => {
  const cacheKey = maybeData ? cacheKeyOrData : 'default';
  const data = maybeData || cacheKeyOrData;
  return put('dashboard', {
    key: `summary:${cacheKey || 'default'}`,
    ...data,
    cachedAt: new Date().toISOString(),
  });
};
export const getOfflineDashboard = (cacheKey) => get('dashboard', `summary:${cacheKey || 'default'}`);

// ── SETTINGS ──────────────────────────────────────────────────────────────────

export const cacheSettings = (settings) => put('settings', { key: 'workspace', ...settings });
export const getOfflineSettings = () => get('settings', 'workspace');
